import React from 'react';
import { useInView } from 'react-intersection-observer';
import { MessageCircle, Palette, CheckCircle2 } from 'lucide-react';
import Reveal from './Reveal';
import SectionHeading from './SectionHeading';

export interface HowItWorksStep {
  title: string;
  text: string;
}

interface HowItWorksProps {
  steps: HowItWorksStep[];
  /** Section heading. Defaults to "How it works". */
  title?: string;
  /** Optional anchor id on the <section>. */
  id?: string;
  /** Black band by default; "light" is the same layout on white, for pages whose neighbouring sections are dark. */
  tone?: 'dark' | 'light';
}

type StepIcon = React.ComponentType<{ className?: string; strokeWidth?: number }>;

// Iconoir "peace-hand" (MIT), drawn as strokes so its weight matches the lucide icons.
const PeaceHand: StepIcon = ({ className, strokeWidth = 1.5 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M14.1488 9.47163V3.61153C14.1488 2.72151 13.4273 2 12.5373 2V2C11.6473 2 10.9258 2.72151 10.9258 3.61153V8.44611" />
    <path d="M16.346 12.841L18.5217 5.58862C18.7755 4.74265 18.2886 3.85248 17.4394 3.60984V3.60984C16.5943 3.3684 15.7142 3.8609 15.4779 4.70743L14.1484 9.47149" />
    <path d="M7.61935 9.24985L8.67489 11.5913C9.03961 12.4003 8.68159 13.352 7.87404 13.72C7.06183 14.0901 6.10347 13.7296 5.73663 12.9159L4.68109 10.5745C4.31637 9.76542 4.67439 8.81376 5.48193 8.44574C6.29415 8.07559 7.25251 8.43614 7.61935 9.24985Z" />
    <path d="M11.7192 12.2615V12.2615C11.9239 11.694 11.8998 11.0692 11.6518 10.5192L10.5787 8.13874C10.2181 7.33892 9.27613 6.98454 8.4778 7.34836V7.34836C7.66469 7.71892 7.31885 8.68832 7.71382 9.48986L7.84946 9.76511" />
    <path d="M13.8566 17.6767L14.3487 16.6927C14.3976 16.5947 14.3461 16.4763 14.241 16.4454L10.6903 15.4011C9.97853 15.1918 9.51797 14.5038 9.59563 13.766V13.766C9.68372 12.9292 10.4284 12.3188 11.2662 12.3968L16.0542 12.8422C16.0542 12.8422 19.8632 13.4282 18.5447 17.2372C17.2262 21.0463 16.7867 22.3648 13.8566 22.3648C11.9521 22.3648 9.16855 22.3648 9.16855 22.3648H8.87555C6.52912 22.3648 4.62697 20.4627 4.62697 18.1163V18.1163L4.48047 9.91211" />
  </svg>
);

// The same four icons on every page, by position.
const ICONS: StepIcon[] = [MessageCircle, Palette, CheckCircle2, PeaceHand];

const RADIUS = 62;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const HowItWorks: React.FC<HowItWorksProps> = ({ steps, title = 'How it works', id, tone = 'dark' }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  const dark = tone === 'dark';

  return (
    <section id={id} className={`${dark ? 'bg-black text-white' : 'bg-white text-black'} py-20 md:py-24 px-6`}>
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <SectionHeading title={title} tone={dark ? 'white' : 'black'} underline />
        </Reveal>
        <ol ref={ref} className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {steps.map((step, i) => {
            const Icon = ICONS[i % ICONS.length];
            const fraction = (i + 1) / steps.length;
            return (
              <li key={step.title}>
                <Reveal delay={i * 90} className="flex flex-col items-center text-center">
                  <div className="relative w-[132px] h-[132px]">
                    <svg viewBox="0 0 132 132" className="w-full h-full -rotate-90" aria-hidden="true">
                      <circle cx="66" cy="66" r={RADIUS} fill="none" stroke={dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'} strokeWidth={3} />
                      <circle
                        cx="66"
                        cy="66"
                        r={RADIUS}
                        fill="none"
                        stroke="#78BE20"
                        strokeWidth={3}
                        strokeLinecap="round"
                        strokeDasharray={CIRCUMFERENCE}
                        strokeDashoffset={inView ? CIRCUMFERENCE * (1 - fraction) : CIRCUMFERENCE}
                        className="transition-[stroke-dashoffset] duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
                        style={{ transitionDelay: `${i * 120}ms` }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <Icon className="w-8 h-8 text-[#78BE20]" strokeWidth={1.5} />
                      <span className="font-montserrat font-extrabold text-xl leading-none mt-1.5">{i + 1}</span>
                    </div>
                  </div>
                  <h3 className="font-montserrat font-bold text-lg mt-6">{step.title}</h3>
                  <p className={`font-poppins text-sm leading-6 mt-2 max-w-[260px] ${dark ? 'text-white/70' : 'text-black/70'}`}>
                    {step.text}
                  </p>
                </Reveal>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

export default HowItWorks;
